/**
 * SwipeableTabs Component
 * Modern swipeable tab interface for mobile
 * Uses Framer Motion for smooth animations
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTabSwipe } from '../../hooks/useSwipeGesture';
import { detectMobileDevice, triggerHapticFeedback } from '../../utils/mobile/mobile-utils';

const SWIPE_CONFIDENCE_THRESHOLD = 20000; // Increased from 10000 for stricter swipe detection
const SWIPE_POWER_THRESHOLD = (offset, velocity) => {
  return Math.abs(offset) * velocity;
};

export function SwipeableTabs({
  tabs,
  activeTab,
  onTabChange,
  className = '',
  tabClassName = '',
  contentClassName = '',
  showIndicator = true,
  edgeOnly = true, // Only allow swipes from screen edges by default
}) {
  const [[page, direction], setPage] = useState([activeTab, 0]);

  const paginate = (newDirection) => {
    const newPage = page + newDirection;
    if (newPage >= 0 && newPage < tabs.length) {
      setPage([newPage, newDirection]);
      onTabChange?.(newPage);
    }
  };

  const swipeHandlers = useTabSwipe({
    currentTab: page,
    totalTabs: tabs.length,
    onTabChange: (newTab) => {
      const newDirection = newTab > page ? 1 : -1;
      setPage([newTab, newDirection]);
      onTabChange?.(newTab);
    },
    enabled: true,
    edgeOnly,
  });

  const variants = {
    enter: (direction) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? '100%' : '-100%',
      opacity: 0,
    }),
  };

  const swipeTransition = {
    x: { type: 'spring', stiffness: 300, damping: 30 },
    opacity: { duration: 0.2 },
  };

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Tab Headers */}
      <div className="relative flex border-b border-[var(--theme-border)] bg-[var(--theme-card)]">
        <div className="flex w-full overflow-x-auto scroll-container">
          {tabs.map((tab, index) => (
            <button
              key={tab.id || index}
              onClick={() => {
                const newDirection = index > page ? 1 : -1;
                setPage([index, newDirection]);
                onTabChange?.(index);
              }}
              className={`
                tab-button
                flex-1 min-w-[100px] px-4 py-3
                text-sm font-medium
                transition-colors duration-200
                ${
                  page === index
                    ? 'text-[var(--theme-primary)]'
                    : 'text-[var(--theme-secondary)] hover:text-[var(--theme-foreground)]'
                }
                ${tabClassName}
              `}
              style={{ touchAction: 'manipulation' }}
              aria-selected={page === index}
              role="tab"
            >
              {tab.icon && <span className="mr-2">{tab.icon}</span>}
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Active Tab Indicator */}
        {showIndicator && (
          <motion.div
            className="absolute bottom-0 left-0 h-0.5 bg-[var(--theme-primary)]"
            initial={false}
            animate={{
              x: `${(page / tabs.length) * 100}%`,
              width: `${100 / tabs.length}%`,
            }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          />
        )}
      </div>

      {/* Tab Content */}
      <div
        className={`relative flex-1 overflow-hidden ${contentClassName}`}
        {...swipeHandlers}
      >
        <AnimatePresence initial={false} custom={direction} mode="sync">
          <motion.div
            key={page}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={swipeTransition}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.2}
            onDragEnd={(e, { offset, velocity }) => {
              const swipe = SWIPE_POWER_THRESHOLD(offset.x, velocity.x);

              if (swipe < -SWIPE_CONFIDENCE_THRESHOLD) {
                paginate(1);
              } else if (swipe > SWIPE_CONFIDENCE_THRESHOLD) {
                paginate(-1);
              }
            }}
            className="absolute inset-0 overflow-y-auto scroll-container"
          >
            {tabs[page]?.content}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Optional: Tab Dots Indicator (mobile style) */}
      {tabs.length > 1 && (
        <div className="flex justify-center gap-2 py-3 md:hidden">
          {tabs.map((tab, index) => (
            <motion.button
              key={tab.id || tab.label || `tab-${index}`}
              onClick={() => {
                const newDirection = index > page ? 1 : -1;
                setPage([index, newDirection]);
                onTabChange?.(index);
              }}
              className={`
                w-2 h-2 rounded-full transition-all duration-200
                ${
                  page === index
                    ? 'bg-[var(--theme-primary)] w-6'
                    : 'bg-[var(--theme-border)]'
                }
              `}
              aria-label={`Go to ${tabs[index].label}`}
              style={{ touchAction: 'manipulation' }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * SwipeablePages Component
 * Page-level swipe navigation for main app navigation
 * Integrates with React Router for seamless page transitions
 */

// Page order configuration for swipe navigation
const SWIPEABLE_PAGES = [
  { path: '/library', index: 0 },
  { path: '/insights', index: 1 },
  { path: '/profile', index: 2 },  // Also matches '/'
  { path: '/journal/new', index: 3 },
  { path: '/settings', index: 4 },
];

export function SwipeablePages({ children, modalsOpen = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isNavigatingRef = useRef(false);

  // Detect if user is on mobile device
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const device = detectMobileDevice();
    setIsMobile(device.isMobile || device.hasTouch);
  }, []);

  // Determine current page index from route - memoized to prevent recalculation
  const getCurrentPageIndex = useCallback((pathname) => {
    if (pathname === '/' || pathname === '/profile') return 2;
    const page = SWIPEABLE_PAGES.find(p => pathname.startsWith(p.path));
    return page?.index ?? 2; // Default to Profile (index 2) instead of -1
  }, []);

  // Initialize state from current route (eliminate race condition)
  const [[pageIndex, direction], setPageIndex] = useState(() => {
    const initialIndex = getCurrentPageIndex(location.pathname);
    return [initialIndex, 0];
  });

  // Update page index when route changes
  // (no animation, just tracking current page for swipe navigation)
  useEffect(() => {
    if (isNavigatingRef.current) {
      isNavigatingRef.current = false;
      return;
    }

    const newIndex = getCurrentPageIndex(location.pathname);
    if (newIndex !== pageIndex) {
      setPageIndex([newIndex, 0]); // Always direction 0 (no animation)
    }
  }, [location.pathname, pageIndex, getCurrentPageIndex]);

  const triggerHaptic = () => {
    triggerHapticFeedback('light');
  };

  // Navigate to adjacent page
  const navigateToPage = (newIndex) => {
    if (newIndex < 0 || newIndex >= SWIPEABLE_PAGES.length) return;

    const targetPage = SWIPEABLE_PAGES[newIndex];
    const newDirection = newIndex > pageIndex ? 1 : -1;

    isNavigatingRef.current = true;
    setPageIndex([newIndex, newDirection]);
    navigate(targetPage.path);
    triggerHaptic();
  };

  // Swipe gesture handlers (only enabled on mobile)
  const swipeHandlers = useTabSwipe({
    currentTab: pageIndex,
    totalTabs: SWIPEABLE_PAGES.length,
    onTabChange: navigateToPage,
    enabled: isMobile && !modalsOpen, // Only enabled on mobile and when modals are closed
    edgeOnly: true, // Only allow swipes from screen edges
  });

  // Render children directly without animation
  return (
    <div
      className="w-full"
      {...(isMobile ? swipeHandlers : {})} // Only attach swipe handlers on mobile
    >
      {children}
    </div>
  );
}

export default SwipeableTabs;
